/**
 * The app-side binding of the dictation widget's native desktop-bridge port.
 *
 * Ruling 25 (`docs/monorepo-ui-floor-rulings/25-tauri-bridges-cross-as-desktop-bridge-ports.md`)
 * generalises ruling 8 from `@/tauri/rpa` to every desktop bridge: package UI
 * does not import `src/tauri/**`, and a native desktop operation crosses as an
 * injected desktop-bridge port supplied by the app composition point. This module
 * is that binding for the widget window — after it, this is the only file in the
 * widget cone that IMPORTS the bridge, and the surface it feeds
 * (`DictationWidgetPage` and its hooks) takes the port instead.
 *
 * THE ANNOTATION IS THE PROOF. `DictationWidgetPort` is declared
 * structurally in `@heidi/dictate/contracts` because that door may not name a
 * Tauri type, so nothing else checks that the real bridge functions actually
 * fit it. The `: DictationWidgetPort` annotation below is that check, and
 * it is deliberately not a cast: a cast would assert the fit instead of proving
 * it, and the day a bridge signature changes the cast would still compile.
 *
 * EVERY MEMBER IS A REFERENCE, NEVER A WRAPPER. Each bridge function
 * short-circuits on `!isTauri()` before it reaches `invoke`/`listen`, so
 * forwarding the function forwards the guard. A re-implemented default would
 * change web behaviour silently — nothing renders this page outside Tauri
 * today, so no test would catch it. `tauri-widget-port.test.ts` pins the
 * references by identity for exactly that reason.
 */

import {
  listenWidgetEscapePressed,
  listenWidgetMouseEntered,
  listenWidgetMouseExited,
  widgetContentReady,
  widgetCopyTranscript,
  widgetHide,
  widgetOpenFeedbackHistory,
  widgetSetHitRect,
  widgetSetKeyboardFocus,
} from '@/tauri/widget/api';
import { type DictationWidgetPort } from '@heidi/dictate/contracts';

/**
 * Declared at module scope, not assembled at the render site.
 *
 * The hooks behind this port subscribe on the port member's identity, so an
 * object literal built inside JSX would detach and re-attach three native
 * listeners on every render of the widget window.
 */
export const tauriWidgetPort: DictationWidgetPort = {
  copyTranscript: widgetCopyTranscript,
  signalContentReady: widgetContentReady,
  hide: widgetHide,
  openFeedbackHistory: widgetOpenFeedbackHistory,
  setHitRect: widgetSetHitRect,
  setKeyboardFocus: widgetSetKeyboardFocus,
  onMouseEntered: listenWidgetMouseEntered,
  onMouseExited: listenWidgetMouseExited,
  onEscapePressed: listenWidgetEscapePressed,
};
